import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { API, ax, errorText } from "./common";

const BASE = `${API}/admin/file-backup`;
const MAX_EVERY = { day: 31, week: 54, month: 12 };
const NEW_RULE = { enabled: true, unit: "day", every: 1, time: "03:00", weekday: 0, day: 1, keep_deleted_days: 90 };
const READ_ONLY = ["id", "created_at", "last_run_at", "last_status", "last_error", "last_copied", "last_pruned", "next_run_at"];
const inRange = (v, lo, hi) => Number.isInteger(Number(v)) && Number(v) >= lo && Number(v) <= hi;
const card = "bg-white rounded-2xl border border-slate-200 shadow-sm p-4 space-y-3";

// Platform administrator only: rules that copy the uploaded files (labels, photos) into the R2 backup bucket.
export default function FileBackupPanel({ showMsg, refreshTick = 0 }) {
  const { t, lang } = useLang();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const when = (iso) => (iso ? new Date(iso).toLocaleString(lang === "tr" ? "tr-TR" : "en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(BASE, ax);
      setStatus(data);
    } catch (e) { showMsg("error", errorText(e, t("adm.error"))); }
  }, [showMsg, t]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (refreshTick) load(); }, [refreshTick]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };

  const openRule = (rule) => {
    setEditingId(rule?.id || null);
    setForm(rule ? { ...NEW_RULE, ...rule } : NEW_RULE);
  };

  const saveRule = () => run(async () => {
    const body = { ...form, every: Number(form.every), weekday: Number(form.weekday), day: Number(form.day), keep_deleted_days: Number(form.keep_deleted_days) };
    READ_ONLY.forEach(k => delete body[k]);
    try {
      if (editingId) await axios.put(`${BASE}/rules/${editingId}`, body, ax);
      else await axios.post(`${BASE}/rules`, body, ax);
      setForm(null);
    } catch (e) { showMsg("error", errorText(e, t("adm.error"))); }
    load();
  });

  const deleteRule = (rule) => run(async () => {
    try { await axios.delete(`${BASE}/rules/${rule.id}`, ax); } catch (e) { showMsg("error", errorText(e, t("adm.error"))); }
    load();
  });

  const runNow = (rule) => run(async () => {
    try {
      await axios.post(`${BASE}/rules/${rule.id}/run`, {}, ax);
      showMsg("success", t("adm.fb.ran"));
    } catch (e) { showMsg("error", errorText(e, t("adm.error"))); }
    load();
  });

  const describe = (r) => {
    const time = r.time;
    if (r.unit === "day") return r.every === 1 ? t("adm.db.rule_daily", { time }) : t("adm.db.rule_days", { n: r.every, time });
    if (r.unit === "week") return t("adm.db.rule_weeks", { n: r.every, day: t(`adm.db.wd.${r.weekday}`), time });
    return t("adm.db.rule_months", { n: r.every, day: r.day, time });
  };

  const formValid = form && /^\d{2}:\d{2}$/.test(form.time) && inRange(form.every, 1, MAX_EVERY[form.unit]) && inRange(form.keep_deleted_days, 1, 3650)
    && (form.unit !== "month" || inRange(form.day, 1, 28));

  if (!status) return null;

  return (
    <div className={card}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="font-semibold text-slate-900 text-sm">{t("adm.fb.title")}</p>
        <Button size="sm" onClick={() => openRule(null)} className="gap-1.5 bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4" /> {t("adm.fb.add_rule")}
        </Button>
      </div>
      {!status.bucket_ready && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">{t("adm.db.needs_bucket")}</p>}
      <p className="text-xs text-slate-500">{t("adm.fb.intro")}</p>
      <div className="rounded-xl border border-slate-200 divide-y divide-slate-50">
        {status.rules.length === 0 && <p className="p-6 text-center text-slate-400 text-sm">{t("adm.db.no_rules")}</p>}
        {status.rules.map(r => (
          <div key={r.id} className="flex items-center gap-3 px-3 py-2 flex-wrap">
            <div className="flex-1 min-w-[12rem]">
              <p className={`text-sm font-semibold ${r.enabled ? "text-slate-900" : "text-slate-400"}`}>
                {describe(r)} <span className="font-normal text-xs text-slate-500">· {t("adm.fb.keep_deleted_n", { n: r.keep_deleted_days })}</span>
              </p>
              <p className="text-xs text-slate-500">
                {t("adm.db.last_run")}: {r.last_status === "error"
                  ? <span className="text-red-700">✗ {when(r.last_run_at)} — {r.last_error}</span>
                  : r.last_status === "ok"
                    ? <span className="text-emerald-700">✓ {when(r.last_run_at)} ({t("adm.fb.result", { copied: r.last_copied ?? 0, pruned: r.last_pruned ?? 0 })})</span>
                    : "—"}
                {" · "}{t("adm.db.next_run")}: {r.enabled ? when(r.next_run_at) : t("adm.inactive")}
              </p>
            </div>
            <Button size="sm" variant="outline" className="h-7" disabled={busy || !status.bucket_ready} onClick={() => runNow(r)}>{t("adm.fb.run_now")}</Button>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-blue-600" onClick={() => openRule(r)} aria-label={t("common.edit")}>
              <Pencil className="w-3.5 h-3.5" />
            </Button>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-600" onClick={() => deleteRule(r)} aria-label={t("common.delete")}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-400">{t("adm.db.timezone")}</p>

      <Dialog open={!!form} onOpenChange={(open) => !open && setForm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t(editingId ? "adm.fb.edit_rule" : "adm.fb.add_rule")}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Switch id="fb-enabled" checked={form.enabled} onCheckedChange={v => setForm(f => ({ ...f, enabled: v }))} />
                <Label htmlFor="fb-enabled">{t("adm.active")}</Label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>{t("adm.db.unit")}</Label>
                  <Select value={form.unit} onValueChange={v => setForm(f => ({ ...f, unit: v }))}>
                    <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["day", "week", "month"].map(u => <SelectItem key={u} value={u}>{t(`adm.db.unit_${u}`)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="fb-every">{t(`adm.db.every_${form.unit}`)}</Label>
                  <Input id="fb-every" type="number" min={1} max={MAX_EVERY[form.unit]} value={form.every} className="h-10"
                    onChange={e => setForm(f => ({ ...f, every: e.target.value }))} />
                </div>
                {form.unit === "week" && (
                  <div className="space-y-1.5">
                    <Label>{t("adm.db.weekday")}</Label>
                    <Select value={String(form.weekday)} onValueChange={v => setForm(f => ({ ...f, weekday: Number(v) }))}>
                      <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[0, 1, 2, 3, 4, 5, 6].map(d => <SelectItem key={d} value={String(d)}>{t(`adm.db.wd.${d}`)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {form.unit === "month" && (
                  <div className="space-y-1.5">
                    <Label htmlFor="fb-day">{t("adm.db.month_day")}</Label>
                    <Input id="fb-day" type="number" min={1} max={28} value={form.day} className="h-10"
                      onChange={e => setForm(f => ({ ...f, day: e.target.value }))} />
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label htmlFor="fb-time">{t("adm.db.time")}</Label>
                  <Input id="fb-time" type="time" value={form.time} className="h-10"
                    onChange={e => setForm(f => ({ ...f, time: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="fb-keep">{t("adm.fb.keep_deleted")}</Label>
                  <Input id="fb-keep" type="number" min={1} max={3650} value={form.keep_deleted_days} className="h-10"
                    onChange={e => setForm(f => ({ ...f, keep_deleted_days: e.target.value }))} />
                </div>
              </div>
              <p className="text-xs text-slate-500">{formValid ? describe({ ...form, every: Number(form.every) }) : t("adm.db.rule_invalid")}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>{t("common.cancel")}</Button>
            <Button onClick={saveRule} disabled={busy || !formValid} className="bg-blue-600 hover:bg-blue-700">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
